import React, { useState } from "react";
import ModifyParticipantGatherings from "../../api/ModifyParticipantGatherings"; // Unified API for add/remove
import Modal from "../Modal/Modal";
import ModifyGathering from "../../api/ModifyGathering";
import ParticipantInstructions from "../ParticipantInstructions/ParticipantInstructions";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../../config/FirebaseConfig";

const DescriptionCell = ({ description }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <div onClick={toggleExpanded} style={{ cursor: "pointer" }}>
      <div style={expanded ? {} : { height: "50px", overflow: "hidden" }}>
        {description}
      </div>
      <div style={{ color: "#2183A6", fontSize: "0.9em", marginTop: "4px" }}>
        {expanded ? "hide full description" : "click to expand"}
      </div>
    </div>
  );
};

const ParticipantTab = ({
  participantType,
  participants = [],
  gatheringID,
  gatheringReferenceID,
  updateParticipants, // Callback to update participants in the parent component
  priorityVendors, // Array of Priority Vendor IDs from Gathering
  updatePriorityVendors, // Callback to update Priority Vendors in the parent component
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentParticipantReferenceID, setCurrentParticipantReferenceID] =
    useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newParticipantReferenceID, setNewParticipantReferenceID] =
    useState("");

  const [isAddingParticipant, setIsAddingParticipant] = useState(false);

  const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);

  console.log(priorityVendors);

  // Confirm remove modal handlers
  const handleRemoveClick = (participantReferenceID) => {
    setCurrentParticipantReferenceID(participantReferenceID);
    setShowConfirmModal(true);
  };

  const confirmRemoval = async () => {
    if (!currentParticipantReferenceID) return;

    setIsRemovingParticipant(true);

    try {
      const result = await ModifyParticipantGatherings(
        currentParticipantReferenceID,
        gatheringReferenceID,
        participantType,
        "remove",
      );

      if (result.success) {
        alert(`${participantType} removed successfully.`);
        updateParticipants(
          participants.filter(
            (participant) =>
              participant.referenceID !== currentParticipantReferenceID,
          ),
        );
      } else {
        alert(`Failed to remove the ${participantType}.`);
      }
    } catch (error) {
      console.error(`Error removing ${participantType}:`, error);
      alert("An unexpected error occurred.");
    } finally {
      setShowConfirmModal(false);
      setCurrentParticipantReferenceID(null);
      setIsRemovingParticipant(false);
    }
  };

  const cancelRemoval = () => {
    setShowConfirmModal(false);
    setCurrentParticipantReferenceID(null);
  };

  // Add participant modal handlers
  const openAddModal = () => setShowAddModal(true);
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewParticipantReferenceID("");
  };

  const fetchParticipantByReferenceID = async (participantReferenceID) => {
    const collectionName =
      participantType === "Vendor"
        ? "Vendors"
        : participantType === "Artist"
          ? "Artists"
          : "EventParticipants";

    const participantQuery = query(
      collection(firestore, collectionName),
      where("ReferenceID", "==", participantReferenceID),
    );

    let snapshot = await getDocs(participantQuery);

    if (snapshot.empty) {
      const lowercaseReferenceQuery = query(
        collection(firestore, collectionName),
        where("referenceID", "==", participantReferenceID),
      );

      snapshot = await getDocs(lowercaseReferenceQuery);
    }

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      name: data.Name || data.name || "",
      email: data.Email || data.email || "",
      description: data.Description || data.description || "",
      website: data.Website || data.website || "",
      imagePath: data.ImagePath || data.imagePath || "",
      referenceID:
        data.ReferenceID || data.referenceID || participantReferenceID,
    };
  };

  const handleAddParticipant = async () => {
    if (isAddingParticipant) {
      return;
    }

    const trimmedReferenceID = newParticipantReferenceID.trim();

    if (!trimmedReferenceID) {
      alert("Please enter a Participant Reference ID.");
      return;
    }

    const alreadyAdded = participants.some(
      (participant) => participant.referenceID === trimmedReferenceID,
    );

    if (alreadyAdded) {
      alert(`${participantType} is already added to this event.`);
      return;
    }

    setIsAddingParticipant(true);

    try {
      const result = await ModifyParticipantGatherings(
        trimmedReferenceID,
        gatheringReferenceID,
        participantType,
        "add",
      );

      if (result.success) {
        const addedParticipant =
          await fetchParticipantByReferenceID(trimmedReferenceID);

        if (addedParticipant) {
          updateParticipants([...participants, addedParticipant]);
        } else {
          updateParticipants([
            ...participants,
            {
              referenceID: trimmedReferenceID,
              name: "New Participant",
              email: "",
              description: "",
            },
          ]);
        }

        alert(`${participantType} added successfully.`);
        closeAddModal();
      } else {
        alert(`Failed to add the ${participantType}.`);
      }
    } catch (error) {
      console.error(`Error adding ${participantType}:`, error);
      alert("An unexpected error occurred.");
    } finally {
      setIsAddingParticipant(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (!gatheringReferenceID) {
      alert("No Reference ID available to copy!");
      return;
    }

    navigator.clipboard
      .writeText(gatheringReferenceID)
      .then(() => alert("Reference ID copied to clipboard!"))
      .catch(() => alert("Failed to copy Reference ID."));
  };

  // Handle priority toggle
  const togglePriority = async (participantID) => {
    const isPriority = priorityVendors.includes(participantID);
    const updatedPriorityVendors = isPriority
      ? priorityVendors.filter((id) => id !== participantID) // Remove if already priority
      : [...priorityVendors, participantID]; // Add if not priority

    try {
      // Update the PriorityVendors array in Firestore
      await ModifyGathering(gatheringID, {
        priorityVendors: updatedPriorityVendors,
      });
      updatePriorityVendors(updatedPriorityVendors); // Update state in parent
      alert(
        `${participantType} ${
          isPriority ? "removed from" : "added to"
        } priority list.`,
      );
    } catch (error) {
      console.error("Error toggling priority:", error);
      alert("Failed to update priority status. Please try again.");
    }
  };

  return (
    <div className="input-container">
      <h3 className="tab-title">{participantType}s</h3>
      <p>
        Share the following {participantType} ID with your invited{" "}
        {participantType.toLowerCase()}s. They can use it to create an account
        and register with your event:
      </p>
      <p>{gatheringReferenceID}</p>
      <p>
        Please only share this code with trusted invitees. Any participant can
        be removed from your event at any time in the table below.
      </p>
      <button
        onClick={handleCopyToClipboard}
        style={{ width: "50%", marginBottom: "10px" }}
      >
        Copy to Clipboard
      </button>
      <ParticipantInstructions gatheringReferenceID={gatheringReferenceID} />
      <button
        onClick={openAddModal}
        style={{ width: "50%", marginBottom: "10px" }}
      >
        Add {participantType}
      </button>
      {participants.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.referenceID}>
                  <td>{participant.name}</td>
                  <td>{participant.email}</td>
                  <td>
                    {" "}
                    <DescriptionCell
                      description={participant.description || ""}
                    />{" "}
                  </td>
                  <td>
                    <button
                      onClick={() => handleRemoveClick(participant.referenceID)}
                      style={{ margin: "3px", width: "100%" }}
                    >
                      Remove
                    </button>
                    {participantType === "Vendor" && (
                      <div>
                        <button
                          onClick={() =>
                            togglePriority(participant.referenceID)
                          }
                          style={{ margin: "3px", width: "100%" }}
                        >
                          {priorityVendors.includes(participant.referenceID)
                            ? "Remove Priority"
                            : "Mark as Priority"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showConfirmModal && (
        <Modal isOpen={showConfirmModal} onClose={cancelRemoval}>
          <div>
            <h4>
              Are you sure you want to remove this{" "}
              {participantType.toLowerCase()}?
            </h4>
            <button onClick={confirmRemoval} disabled={isRemovingParticipant}>
              {isRemovingParticipant ? "Removing participant..." : "Yes"}
            </button>{" "}
            <button onClick={cancelRemoval}>No</button>
          </div>
        </Modal>
      )}
      {showAddModal && (
        <Modal isOpen={showAddModal} onClose={closeAddModal}>
          <h4>Add {participantType} by Reference ID</h4>
          <p>Contact the Participant for their unique Reference ID.</p>
          <input
            type="text"
            className="input-field"
            value={newParticipantReferenceID}
            onChange={(e) => setNewParticipantReferenceID(e.target.value)}
            placeholder={`Enter ${participantType} Reference ID`}
            disabled={isAddingParticipant}
          />

          <button onClick={handleAddParticipant} disabled={isAddingParticipant}>
            {isAddingParticipant ? "Adding..." : "Submit"}
          </button>

          {isAddingParticipant && (
            <p style={{ marginTop: 8, fontSize: "0.9rem", color: "#4a5568" }}>
              Adding {participantType.toLowerCase()}. Please wait...
            </p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ParticipantTab;
